/**
 * Chart data transforms for the Main chart (Charts & Models modules).
 *
 * Keep this module dependency-free — it's called from hot memoised render paths.
 */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO 8601 week number for a given date. */
export const getISOWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const validNumbers = (items, key) =>
  items.map(i => i[key]).filter(v => v != null && isFinite(v));

const avg = (items, key = 'value') => {
  const nums = validNumbers(items, key);
  if (!nums.length) return null;
  return nums.reduce((s, v) => s + v, 0) / nums.length;
};

/**
 * Aggregate a sorted list of daily-cadence items into period buckets.
 *
 * Each bucket is reduced to a single chart point with proper OHLC semantics:
 *   - open  = open of the first (chronologically earliest) item in the bucket
 *   - high  = max of all items' high
 *   - low   = min of all items' low
 *   - close = close of the last (chronologically latest) item in the bucket
 *   - value = average of items' value (used by line/area/bar views)
 *
 * Anything else on the source items is dropped — comparison_X_value etc. are
 * stitched in upstream after aggregation, so we don't need to forward them.
 */
const bucket = (rawData, keyFn, labelFn, anchorDateFn) => {
  const groups = {};
  rawData.forEach(item => {
    const date = new Date(item.date);
    const key = keyFn(date);
    if (!groups[key]) groups[key] = { items: [], date };
    groups[key].items.push(item);
  });

  return Object.keys(groups)
    .sort()
    .map(key => {
      const group = groups[key];
      // Items inside a bucket aren't guaranteed to be in date order — sort so
      // open/close pick the correct ends.
      const sorted = [...group.items].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const highs = validNumbers(sorted, 'high');
      const lows = validNumbers(sorted, 'low');
      const anchor = anchorDateFn(group.date, sorted);

      return {
        date: anchor.toISOString(),
        xAxisLabel: labelFn(group.date, sorted),
        value: avg(sorted, 'value'),
        open: first.open ?? first.value ?? null,
        high: highs.length ? Math.max(...highs) : (first.high ?? first.value ?? null),
        low: lows.length ? Math.min(...lows) : (first.low ?? first.value ?? null),
        close: last.close ?? last.value ?? null,
        volume: avg(sorted, 'volume') ?? 0,
      };
    });
};

/**
 * Reshape a daily-cadence series into the cadence the user picked.
 *
 * - daily: pass-through
 * - weekly / monthly / quarterly / yearly: bucket by the corresponding period
 *   and average the numeric `value` per bucket. The xAxisLabel is derived for
 *   each bucket so the chart's X axis stays readable at any frequency.
 */
export const aggregateDataByFrequency = (rawData, frequency) => {
  if (!rawData || rawData.length === 0) return [];
  if (frequency === 'daily') return rawData;

  if (frequency === 'weekly') {
    return bucket(
      rawData,
      (d) => `${d.getFullYear()}-W${String(getISOWeek(d)).padStart(2, '0')}`,
      (d) => `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`,
      (d) => d, // anchor = first day seen inside the week
    );
  }

  if (frequency === 'monthly') {
    return bucket(
      rawData,
      (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      (d) => MONTH_NAMES[d.getMonth()],
      (d) => new Date(d.getFullYear(), d.getMonth(), 1),
    );
  }

  if (frequency === 'quarterly') {
    return bucket(
      rawData,
      (d) => `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`,
      (d) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`,
      (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1),
    );
  }

  if (frequency === 'yearly') {
    return bucket(
      rawData,
      (d) => `${d.getFullYear()}`,
      (d) => `${d.getFullYear()}`,
      (d) => new Date(d.getFullYear(), 0, 1),
    );
  }

  return rawData;
};

/** Canonical period key for a date at a given frequency. */
const periodKey = (date, frequency) => {
  const d = date instanceof Date ? date : new Date(date);
  switch (frequency) {
    case 'weekly':
      return `${d.getFullYear()}-W${String(getISOWeek(d)).padStart(2, '0')}`;
    case 'monthly':
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    case 'quarterly':
      return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
    case 'yearly':
      return `${d.getFullYear()}`;
    case 'daily':
    default:
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
};

/** For daily frequency only: shift Sat/Sun article dates to the following Monday. */
const carryWeekendToMonday = (iso) => {
  if (!iso) return iso;
  const d = new Date(iso);
  const day = d.getDay(); // 0 Sun, 6 Sat
  if (day === 0) d.setDate(d.getDate() + 1);
  else if (day === 6) d.setDate(d.getDate() + 2);
  return d.toISOString().split('T')[0];
};

/**
 * Sub-period key — used to group articles *within* a data point's tooltip.
 * Spec: "For higher frequencies, news items are grouped one frequency level
 * below the selected data frequency."
 *
 * Returned value is a short human label suitable for the tooltip sub-heading.
 */
const subPeriodLabel = (date, frequency) => {
  const d = date instanceof Date ? date : new Date(date);
  switch (frequency) {
    case 'weekly':
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
    case 'monthly':
      return `Week ${getISOWeek(d)}`;
    case 'quarterly':
      return MONTH_NAMES[d.getMonth()];
    case 'yearly':
      return `Q${Math.floor(d.getMonth() / 3) + 1}`;
    case 'daily':
    default:
      return null;
  }
};

/**
 * Match insight/news articles to chart data points for the current frequency.
 *
 * Returns a Map keyed by the data point's period key. Each entry contains:
 *   - volume: number of articles matched to that period
 *   - articles: the full list of matched articles (sorted newest-first)
 *   - subGroups: articles grouped one level below the main frequency, for the
 *     tooltip's expanded view (empty for daily).
 */
export const matchNewsToDataPoints = (insights, frequency) => {
  const matches = new Map();
  if (!Array.isArray(insights) || insights.length === 0) return matches;

  insights.forEach(article => {
    const raw = (article.recent_at || '').split('T')[0];
    if (!raw) return;

    // Weekend carry-forward applies only to daily frequency per spec.
    const effectiveDate = frequency === 'daily' ? carryWeekendToMonday(raw) : raw;
    const key = periodKey(effectiveDate, frequency);

    if (!matches.has(key)) {
      matches.set(key, { volume: 0, articles: [], subGroups: new Map() });
    }
    const entry = matches.get(key);
    entry.volume += 1;
    entry.articles.push(article);

    const subLabel = subPeriodLabel(effectiveDate, frequency);
    if (subLabel) {
      if (!entry.subGroups.has(subLabel)) {
        entry.subGroups.set(subLabel, { label: subLabel, count: 0, articles: [] });
      }
      const sub = entry.subGroups.get(subLabel);
      sub.count += 1;
      sub.articles.push(article);
    }
  });

  // Sort articles newest-first for predictable top-3 selection and flatten subGroups.
  matches.forEach(entry => {
    entry.articles.sort((a, b) => (b.recent_at || '').localeCompare(a.recent_at || ''));
    entry.subGroups = Array.from(entry.subGroups.values()).map(g => ({
      ...g,
      articles: g.articles.sort((a, b) => (b.recent_at || '').localeCompare(a.recent_at || '')),
    }));
  });

  return matches;
};

/** Resolve the lookup key for a chart data point at a given frequency. */
export const dataPointPeriodKey = (pointDate, frequency) => periodKey(pointDate, frequency);


/**
 * Backend-Timeframe (tf) der Kursreihe fuer die gewaehlte Frequenz.
 * Die Zeitraum-Buttons (1J, 5J, Max) bestimmen nur das Fenster; die Frequenz
 * bestimmt die Punktdichte. Vorher hing tf am Button (1J -> Monatswerte), sodass
 * "Daily" trotzdem Monats- oder Jahreswerte zeigte.
 */
export const seriesTfForFrequency = (frequency) => {
  if (frequency === 'weekly') return '1w';
  if (frequency === 'monthly') return '1m';
  return '1d';
};
